import React, {useEffect, useReducer, useState} from 'react';
import * as d3 from 'd3';
import {DateTime} from 'luxon';
import {Button, Checkbox, HTMLSelect, Tooltip, Switch} from '@blueprintjs/core';
import {DateInput} from "@blueprintjs/datetime";
import {DateRangeInput} from "@blueprintjs/datetime";

import {API} from 'aws-amplify';

import GenerationOutagesChart from './GenerationOutagesChart';
import GenerationOutagesMultiSelect from './GenerationOutagesMultiSelect';
import styles from './GenerationOutagesContainer.module.css';
import {DateRangeInput2} from "@blueprintjs/datetime2";

// import outages_data from "./outages-records.json";
var outages_data = [];

const category10 = [ "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd", "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf" ];
const category20b = [ "#393b79", "#5254a3", "#6b6ecf", "#9c9ede", "#637939", "#8ca252", "#b5cf6b", "#cedb9c", "#8c6d31", "#bd9e39", "#e7ba52", "#e7cb94", "#843c39", "#ad494a", "#d6616b", "#e7969c", "#7b4173", "#a55194", "#ce6dbd", "#de9ed6" ]
const category20c = [ "#3182bd", "#6baed6", "#9ecae1", "#c6dbef", "#e6550d", "#fd8d3c", "#fdae6b", "#fdd0a2", "#31a354", "#74c476", "#a1d99b", "#c7e9c0", "#756bb1", "#9e9ac8", "#bcbddc", "#dadaeb", "#636363", "#969696", "#bdbdbd", "#d9d9d9" ];

// So that our charts appear in the same order bottom-to-top we prepare this list
// anything we fetch from the DB that isn't in the list will be appended and show
// at the top of the chart
const generationTypeKeys = ["GEO", "COG", "GAS", "LIQ", "CG", "HYD", "BAT", "WIN", "SOL"];
const defaultGenerationTypeSelection = ["BAT", "CG", "COG", "GAS", "GEO", "LIQ"];

const today = DateTime.now().setZone("Pacific/Auckland").startOf("day");

const initialState = {
    data: [],
    fetchedDateRange: null,
    selectedDateRange: [
        today,
        today.plus({months: 1})
    ],
    chartData: {data: [], keys: [], key: null},
    companies: [],
    companiesSelection: [],
    companiesByGenerationType: {},
    generationTypes: [],
    generationTypesSelection: [],
    stations: [],
    stationSelection: [],
    stationsByCompany: {},
    stationsByGenerationType: {},
    hasFetched: false
};

const ACTIONS = {
    LOAD_DATA: 'load-data',
    DATE_RANGE: 'date-range',
    COMPANY: 'company',
    GENERATION_TYPE: 'generation_type',
    STATION: 'station'
};

function makeDictOfSet() {
    return new Proxy({}, {
        get(target, key) {
            if (!(key in target)) {
                target[key] = new Set();
            }
            return target[key];
        }
    });
}

const getChartData = (state) => {
    const data = state.data;

    // Let's sort our selections here
    state.generationTypesSelection.sort();
    state.companiesSelection.sort();
    state.stationSelection.sort();

    console.debug("#data", data.length, "[1]");

    console.debug("generation-types", state.generationTypesSelection);
    let filteredData = data.filter(d => state.generationTypesSelection.includes(d.generation_type_id));
    console.debug("#retained", filteredData.length, "[1]");

    console.debug("companies", state.companiesSelection, "[1]");
    let filteredCompanies = new Set(filteredData.map(d => d.generation_participant_id));
    state.companiesSelection = state.companiesSelection.filter(n => filteredCompanies.has(n));
    console.debug("companies", state.companiesSelection, "[2]");

    filteredData = filteredData.filter(d => state.companiesSelection.includes(d.generation_participant_id));
    console.debug("#retained", filteredData.length, "[2]");

    console.debug("stations", state.stationSelection, "[1]");
    let filteredStations = new Set(filteredData.map(d => d.emh_station_code));
    state.stationSelection = state.stationSelection.filter(n => filteredStations.has(n));
    console.debug("stations", state.stationSelection, "[2]");

    filteredData = filteredData.filter(d => state.stationSelection.includes(d.emh_station_code));
    console.debug("#retained", filteredData.length, "[3]");

    console.debug("fetched-date-range", state.fetchedDateRange);
    console.debug("selected-date-range", state.selectedDateRange);
    const firstPeriodTimestamp = state.selectedDateRange[0];
    const lastPeriodTimestamp = state.selectedDateRange[1].endOf("day");

    filteredData = filteredData.filter(d => d.period_timestamp >= firstPeriodTimestamp && d.period_timestamp <= lastPeriodTimestamp);
    console.debug("#retained", filteredData.length, "[4]");

    const grouping_key = "generation_type_id";

    const key_fn = d => {
        return d[grouping_key]
    };

    const filteredKeys = new Set(filteredData.map(d => key_fn(d)));
    const chartKeys = generationTypeKeys.filter(k => filteredKeys.has(k));
    const colourScheme = category10;  // category20b;
    const chartColours = chartKeys.map(k => colourScheme[generationTypeKeys.indexOf(k) % colourScheme.length]);

    const gdata = d3.rollup(filteredData, v => Math.trunc(d3.sum(v, d => d.remaining_capacity_mw)), d => d.period_timestamp, d => key_fn(d));

    const _data = Array.from(gdata, ([k, v]) => {
        let result = Object.fromEntries(v);
        result["period_timestamp"] = k; // a DateTime object
        return result;
    }).sort((a, b) => a.period_timestamp - b.period_timestamp);

    state.chartData = {
        keys: chartKeys,
        data: _data,
        colors: chartColours
    };

    return state;
};

const restoreCompaniesForGenerationType = (state_obj, generationType) => {
    if (generationType) {
        for (const company of state_obj.companiesByGenerationType[generationType]) {
            if (!state_obj.companiesSelection.includes(company)) {
                console.log("restoring selected company", company, "for generation type", generationType);
                state_obj.companiesSelection.push(company);

                restoreStationsForCompany(state_obj, company);
            }
        }
    }
};

const restoreStationsForGenerationType = (state_obj, generationType) => {
    if (generationType) {
        for (const station_code of state_obj.stationsByGenerationType[generationType]) {
            if (!state_obj.stationSelection.includes(station_code)) {
                console.log("restoring selected station", station_code, "for generation type", generationType);
                state_obj.stationSelection.push(station_code);
            }
        }
    }
};

const restoreStationsForCompany = (state_obj, company) => {
    if (company) {
        for (const station_code of state_obj.stationsByCompany[company]) {
            if (!state_obj.stationSelection.includes(station_code)) {
                console.log("restoring selected station", station_code, "for company", company);
                state_obj.stationSelection.push(station_code);
            }
        }
    }
};

const outagesReducer = (state, action) => {
    var new_state = null;
    switch (action.type) {
        case ACTIONS.LOAD_DATA:
            const generationTypes = new Set();
            const companies = new Set();
            const stations = new Set();

            const companiesByGenerationType = makeDictOfSet();
            const stationsByGenerationType = makeDictOfSet();
            const stationsByCompany = makeDictOfSet();
            for (const d of action.data) {
                if(d.generation_participant_id) {
                    generationTypes.add(d.generation_type_id);
                    companies.add(d.generation_participant_id);
                    stations.add(d.emh_station_code);

                    companiesByGenerationType[d.generation_type_id].add(d.generation_participant_id);
                    stationsByGenerationType[d.generation_type_id].add(d.emh_station_code);
                    stationsByCompany[d.generation_participant_id].add(d.emh_station_code);
                }
            }

            // Check we have a key for all generation types already
            for (const generationType of generationTypes) {
                if (!generationTypeKeys.includes(generationType)) {
                    console.info("Adding previously unseen generation type", generationType);
                    generationTypeKeys.push(generationType);
                }
            }

            // On initial load state.hasFetched is false, return the default company/station selection
            if (!state.hasFetched) {
                new_state = {
                    ...state,
                    data: action.data,
                    fetchedDateRange: state.selectedDateRange,
                    companies: [...companies],
                    companiesSelection: [...companies],
                    companiesByGenerationType: companiesByGenerationType,
                    generationTypes: [...generationTypes],
                    generationTypesSelection: defaultGenerationTypeSelection,
                    stations: [...stations],
                    stationSelection: [...stations],
                    stationsByCompany: stationsByCompany,
                    stationsByGenerationType: stationsByGenerationType,
                    hasFetched: true
                };
            }
            // Otherwise, we have done the initial fetch and want to preserve company/station selection
            else {
                new_state = {
                    ...state,
                    data: action.data,
                    fetchedDateRange: state.selectedDateRange
                };
            }
            break;

        case ACTIONS.DATE_RANGE:
            //TODO warn if new date range exceeds current data limits
            // Changing the dateRange will cause LOAD_DATA to dispatch
            new_state = {
                ...state,
                selectedDateRange: [ action.from_date, action.to_date ]
            };
            break;

        case ACTIONS.COMPANY:
            new_state = {
                ...state,
                companiesSelection: action.selection,
            };
            restoreStationsForCompany(new_state, action.added);
            break;

        case ACTIONS.GENERATION_TYPE:
            new_state = {
                ...state,
                generationTypesSelection: action.selection
            };
            restoreCompaniesForGenerationType(new_state, action.added);
            restoreStationsForGenerationType(new_state, action.added);
            break;

        case ACTIONS.STATION:
            new_state = {
                ...state,
                stationSelection: action.selection
            };
            break;

        default:
            throw new Error();
    }
    if (new_state != null) {
        new_state = getChartData(new_state);
    }
    return new_state;
};


function GenerationOutagesContainer(props) {

    const [state, dispatch] = useReducer(outagesReducer, initialState);
    const {
        chartData,
        fetchedDateRange,
        selectedDateRange,
        companies,
        companiesSelection,
        generationTypes,
        generationTypesSelection,
        stations,
        stationSelection
    } = state;

    const [shouldFetch, setShouldFetch] = useState(true);
    const [loading, setLoading] = useState(false);
    const [confirmedOnly, setConfirmedOnly] = useState(true);

    let fetchButtonStyle = {};
    let fetchButtonTooltip = "No data fetched";
    let suggestFetchRequired = fetchedDateRange == null;

    if(fetchedDateRange != null) {
        fetchButtonTooltip = "Fetched: " + fetchedDateRange[0].toISODate() + " .. " + fetchedDateRange[1].toISODate();

        if(selectedDateRange[0] < fetchedDateRange[0] || selectedDateRange[0] > fetchedDateRange[1])
            suggestFetchRequired = true;

        if(selectedDateRange[1] < fetchedDateRange[0] || selectedDateRange[1] > fetchedDateRange[1])
            suggestFetchRequired = true;
    }

    if(!shouldFetch && suggestFetchRequired)
        fetchButtonStyle.backgroundColor = "orange";

    useEffect(() => {
        if (!shouldFetch) return;

        const [fromDate, toDate] = selectedDateRange;

        document.title = "Outage chart";

        async function fetchData() {
            setLoading(true);

            try {
                const response = await API.get('SparcAPI', '/pocp/generation_outages', {
                    queryStringParameters: {
                        from_date: fromDate.toISODate(),
                        to_date: toDate.toISODate(),
                        confirmed_only: confirmedOnly
                    }
                });

                var data = response.map(d => ({
                    ...d,
                    period_timestamp: DateTime.fromISO(d.period_timestamp).setZone("Pacific/Auckland")
                }));

                if (data.length === 0) return;

                dispatch({
                    type: ACTIONS.LOAD_DATA,
                    data: data,
                });
            } finally {
                setLoading(false);
                setShouldFetch(false);
            }

        }

        fetchData();

    }, [shouldFetch]);

    // For DateRangeInput2
    const formatDate = (date) => DateTime.fromJSDate(date).toISODate();
    const parseDate = (str) => DateTime.fromISO(str).toJSDate();

    return (
        <div>
            <div className={styles.flex_container}>

                <div className={styles.flex_child}>
                    <p>Date Range</p>
                    <DateRangeInput2
                        disabled={loading}
                        formatDate={formatDate}
                        parseDate={parseDate}
                        allowSingleDayRange={true}
                        contiguousCalendarMonths={true}
                        onChange={e => dispatch(
                            {
                                type: ACTIONS.DATE_RANGE,
                                from_date: DateTime.fromJSDate(e[0]),
                                to_date: DateTime.fromJSDate(e[1])
                            })
                        }
                        value={[ selectedDateRange[0].toJSDate(), selectedDateRange[1].toJSDate() ]}
                    />
                </div>

                <div className={styles.flex_child}>
                    <Checkbox
                        disabled={loading}
                        checked={confirmedOnly}
                        label="Confirmed only"
                        onChange={e => setConfirmedOnly(e.target.checked)}
                    />
                    <Tooltip content={fetchButtonTooltip}>
                    <Button
                        onClick={e => setShouldFetch(true)}
                        loading={loading}
                        style={fetchButtonStyle}
                    >Fetch data</Button>
                    </Tooltip>
                </div>

            </div>

            <div className={styles.flex_container}>
                <div className={styles.flex_child}>
                    <p>Generation Type</p>
                    <GenerationOutagesMultiSelect
                        type_name={ACTIONS.GENERATION_TYPE}
                        items={generationTypes}
                        selectedItems={generationTypesSelection}
                        dispatch={dispatch}
                        disabled={loading}
                    />
                </div>

                <div className={styles.flex_child}>
                    <p>Generator</p>
                    <GenerationOutagesMultiSelect
                        type_name={ACTIONS.COMPANY}
                        items={companies}
                        selectedItems={companiesSelection}
                        dispatch={dispatch}
                        disabled={loading}
                    />
                </div>

                <div className={styles.flex_child}>
                    <p>Station</p>
                    <GenerationOutagesMultiSelect
                        type_name={ACTIONS.STATION}
                        items={stations}
                        selectedItems={stationSelection}
                        dispatch={dispatch}
                        disabled={loading}
                    />
                </div>
            </div>

            <div className={styles.charts}>
                <h3>Remaining Generation Capacity</h3>
                <div className={styles.chart}>
                    <GenerationOutagesChart
                        key={chartData.key}
                        data={chartData.data}
                        keys={chartData.keys}
                        colors={chartData.colors}
                        useStreamChart={true}
                    />
                </div>
            </div>

        </div>

    );

};

export default GenerationOutagesContainer;
